import { CounterInput } from "@/components/counter-input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetActiveSubscriptionPlans } from "@/hooks/use-subscription";
import { formatPlansForDisplay, PlanDisplay } from "@/lib/plan.utils";
import {
  b2bPlanObject,
  b2bPlanOrder,
  B2BPlanType,
  b2cPlanObject,
  b2cPlanOrder,
  B2CPlanType,
  PlanCategory,
  PlanPrice,
} from "@/pages/billing/pricing-data";
import { DowngradeConfig, useDowngradeStore } from "@/stores/downgrade-store";
import {
  PaymentInterval,
  SubscriptionStatus,
} from "@/types/subscription.types";
import { Warning } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { AlertCircle, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

export default function DowngradeInitiateDialog() {
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  return (
    <Dialog open={showDowngradeModal} onOpenChange={setShowDowngradeModal}>
      <DialogTrigger asChild>
        <Button variant="link" className="text-blue-500 underline">
          Downgrade Plan
        </Button>
      </DialogTrigger>
      <DialogContent className="px-0">
        <DialogHeader className="px-6">
          <DialogTitle>Downgrade Your Plan</DialogTitle>
          <DialogDescription>
            Select the plan you want to downgrade and your preferred option.
          </DialogDescription>
        </DialogHeader>
        <DowngradePlanContent setShowDowngradeModal={setShowDowngradeModal} />
      </DialogContent>
    </Dialog>
  );
}

const DowngradePlanContent = ({
  setShowDowngradeModal,
}: {
  setShowDowngradeModal: (show: boolean) => void;
}) => {
  const [selectedPlanToDowngrade, setSelectedPlanToDowngrade] =
    useState<PlanDisplay | null>(null);

  const [downgradeOption, setDowngradeOption] = useState<"plan" | "cycle">(
    "plan"
  );
  const [downgradeCount, setDowngradeCount] = useState(1);
  const [targetPlanType, setTargetPlanType] = useState<string>("");
  const [interval, setInterval] = useState<string>("");

  const { setDowngradeConfig } = useDowngradeStore();

  const navigate = useNavigate();
  const { data: activePlans } = useGetActiveSubscriptionPlans();

  const formattedPlans: PlanDisplay[] = useMemo(
    () => formatPlansForDisplay(activePlans),
    [activePlans]
  );

  // Get available plans for downgrade
  const getAvailablePlansForDowngrade = useMemo(() => {
    if (!selectedPlanToDowngrade) return [];

    const isB2B = selectedPlanToDowngrade.category === PlanCategory.B2B;
    const planOrder = isB2B ? b2bPlanOrder : b2cPlanOrder;
    const currentPlanIndex = planOrder.findIndex(
      (p) => p === selectedPlanToDowngrade.type
    );

    if (currentPlanIndex <= 0) return [];

    // Get all plans below the current plan
    return planOrder.slice(0, currentPlanIndex).map((planType) => {
      const planObj = isB2B
        ? b2bPlanObject[planType as B2BPlanType]
        : b2cPlanObject[planType as B2CPlanType];
      return {
        type: planType,
        name: planObj.name,
        description: planObj.description,
        features: planObj.features,
        price: planObj.price,
        earlyBirdPrice: planObj.earlyBirdPrice,
      };
    });
  }, [selectedPlanToDowngrade]);

  // Get available billing cycles for downgrade
  const getAvailableCyclesForDowngrade = useMemo(() => {
    if (!selectedPlanToDowngrade) return [];

    const currentCycle = selectedPlanToDowngrade.interval;
    const cycles = Object.values(PaymentInterval);
    const currentCycleIndex = cycles.findIndex((c) => c === currentCycle);

    if (currentCycleIndex <= 0) return [];

    // Get all cycles below the current cycle
    return cycles.slice(0, currentCycleIndex);
  }, [selectedPlanToDowngrade]);

  // Get features that will be lost when downgrading
  const getLostFeatures = useMemo(() => {
    if (
      !selectedPlanToDowngrade ||
      !targetPlanType ||
      downgradeOption !== "plan"
    )
      return [];

    const isB2B = selectedPlanToDowngrade.category === PlanCategory.B2B;
    const currentPlanObj = isB2B
      ? b2bPlanObject[selectedPlanToDowngrade.type as B2BPlanType]
      : b2cPlanObject[selectedPlanToDowngrade.type as B2CPlanType];

    const targetPlanObj = isB2B
      ? b2bPlanObject[targetPlanType as B2BPlanType]
      : b2cPlanObject[targetPlanType as B2CPlanType];

    if (!currentPlanObj || !targetPlanObj) return [];

    // Find features in current plan that are not in target plan
    return currentPlanObj.features.filter(
      (feature) => !targetPlanObj.features.includes(feature)
    );
  }, [selectedPlanToDowngrade, targetPlanType, downgradeOption]);

  // Check if the selected option can be downgraded
  const selectedOptionCanBeDowngraded = selectedPlanToDowngrade
    ? downgradeOption === "plan"
      ? selectedPlanToDowngrade.canDowngradePlanType
      : selectedPlanToDowngrade.canDowngradeCycle
    : false;

  // Determine if any downgrade option is available
  const hasAnyDowngradeOption = selectedPlanToDowngrade
    ? selectedPlanToDowngrade.canDowngradePlanType ||
      selectedPlanToDowngrade.canDowngradeCycle
    : false;

  // Handler for downgrade plan submission
  const handleDowngradePlan = () => {
    if (!selectedPlanToDowngrade) return;

    console.log(selectedPlanToDowngrade);

    // Check if the current option can be downgraded
    const canDowngrade =
      downgradeOption === "plan"
        ? selectedPlanToDowngrade.canDowngradePlanType
        : selectedPlanToDowngrade.canDowngradeCycle;

    if (!canDowngrade) {
      toast.error(
        "This plan cannot be downgraded. Becasue it's the lowest plan in its category."
      );
      return;
    }

    const downgradeConfig: DowngradeConfig = {
      planType: selectedPlanToDowngrade.type,
      subscriptionId: selectedPlanToDowngrade.subscriptionId,
      planId: selectedPlanToDowngrade.planId,
      currentInterval: selectedPlanToDowngrade.interval,
      option: downgradeOption,
      count: downgradeCount,
      ...(downgradeOption === "cycle" && {
        targetInterval: interval,
      }),
      ...(downgradeOption === "plan" && {
        targetPlanType: targetPlanType as B2BPlanType | B2CPlanType,
      }),
    };

    setDowngradeConfig(downgradeConfig);
    navigate({
      to: "/downgrade",
    });
  };

  const isSubmitDisabled =
    !selectedPlanToDowngrade ||
    !selectedOptionCanBeDowngraded ||
    (downgradeOption === "plan" && !targetPlanType) ||
    (selectedPlanToDowngrade &&
      downgradeCount > selectedPlanToDowngrade.quantity);

  return (
    <>
      <ScrollArea className="max-h-[500px] h-full ">
        <div className="flex flex-col gap-4 py-4 px-6">
          <div className="space-y-2">
            <label className="text-sm font-medium">Select Plan</label>
            <Select
              value={selectedPlanToDowngrade?.uniqueId}
              onValueChange={(value) => {
                const plan = formattedPlans.find((p) => p.uniqueId === value);
                if (plan) {
                  setSelectedPlanToDowngrade(plan);
                  // Reset downgrade count when changing plans
                  setDowngradeCount(plan?.quantity || 1);
                  setTargetPlanType("");
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a plan to downgrade">
                  <span className="text-left">
                    {selectedPlanToDowngrade
                      ? `${selectedPlanToDowngrade?.category} - ${selectedPlanToDowngrade?.name} (${selectedPlanToDowngrade?.interval})`
                      : "Select a plan to downgrade"}
                  </span>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {formattedPlans.map((plan) => (
                  <SelectItem
                    key={`${plan.subscriptionId}-${plan.planId}-${plan.interval}`}
                    value={plan.uniqueId}
                    disabled={
                      (!plan.canDowngradePlanType && !plan.canDowngradeCycle) ||
                      plan.status === SubscriptionStatus.PENDING ||
                      !!plan.pendingRequests.cancellation ||
                      !!plan.pendingRequests.downgrade
                    }
                    className="py-2 px-3 w-full disabled:!cursor-not-allowed"
                    wrapInItemText={false}
                  >
                    <div className="w-full pr-8 flex items-center justify-between">
                      <div>
                        <div className="flex items-center justify-between w-full">
                          <span className="font-medium">
                            {plan.category} - {plan.name}
                          </span>
                          {!plan.canDowngradeCycle &&
                            !plan.canDowngradePlanType && (
                              <Badge
                                variant="outline"
                                className="text-xs font-normal ml-2  border-none shadow-none"
                              >
                                Lowest Plan
                              </Badge>
                            )}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <span>{plan.interval}</span>
                          <span>•</span>
                          <span>{plan.quantity} mailboxes</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        {plan.pendingRequests.downgrade && (
                          <Badge
                            variant="default"
                            className="text-xs font-normal ml-2 bg-amber-100 text-amber-500 hover:bg-amber-100 hover:text-amber-500 border-none shadow-none"
                          >
                            Pending Downgrade
                          </Badge>
                        )}
                        {plan.pendingRequests.cancellation && (
                          <Badge
                            variant="default"
                            className="text-xs font-normal ml-2 bg-amber-100 text-amber-500 hover:bg-amber-100 hover:text-amber-500 border-none shadow-none"
                          >
                            Pending Cancellation
                          </Badge>
                        )}
                        {plan.status === SubscriptionStatus.PENDING && (
                          <Badge
                            variant="default"
                            className="text-xs font-normal ml-2 bg-red-100 text-red-500 hover:bg-red-100 hover:text-red-500 border-none shadow-none"
                          >
                            Unpaid
                          </Badge>
                        )}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedPlanToDowngrade && !hasAnyDowngradeOption && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This plan cannot be downgraded. You can only cancel it.
              </AlertDescription>
            </Alert>
          )}

          {selectedPlanToDowngrade && hasAnyDowngradeOption && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Downgrade Option</label>
                <Select
                  value={downgradeOption}
                  onValueChange={(value) => {
                    setDowngradeOption(value as "plan" | "cycle");
                    setTargetPlanType("");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select downgrade option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      value="plan"
                      disabled={!selectedPlanToDowngrade.canDowngradePlanType}
                    >
                      Downgrade Plan Type
                      {!selectedPlanToDowngrade.canDowngradePlanType &&
                        " (Not available for this plan)"}
                    </SelectItem>
                    <SelectItem
                      value="cycle"
                      disabled={!selectedPlanToDowngrade.canDowngradeCycle}
                    >
                      Downgrade Billing Cycle
                      {!selectedPlanToDowngrade.canDowngradeCycle &&
                        " (Already on lowest billing cycle)"}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {!selectedOptionCanBeDowngraded && (
                <Alert variant="warning">
                  <Warning className="h-4 w-4" />
                  <AlertDescription>
                    {downgradeOption === "plan"
                      ? "This is the lowest plan in its category. Consider downgrading the billing cycle instead."
                      : "This plan is already on monthly billing and cannot be downgraded further."}
                  </AlertDescription>
                </Alert>
              )}

              {selectedOptionCanBeDowngraded && downgradeOption === "plan" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Select Target Plan
                  </label>
                  <Select
                    value={targetPlanType}
                    onValueChange={setTargetPlanType}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select target plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailablePlansForDowngrade.map((plan) => (
                        <SelectItem key={plan.type} value={plan.type}>
                          {plan.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {selectedOptionCanBeDowngraded && downgradeOption === "cycle" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Select Target Billing Cycle
                  </label>
                  <Select
                    value={interval}
                    onValueChange={(value) =>
                      setInterval(value as keyof PlanPrice)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select target billing cycle" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableCyclesForDowngrade.map((cycle) => (
                        <SelectItem key={cycle} value={cycle}>
                          {cycle}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium">Mailbox Quantity</label>
                <CounterInput
                  title=""
                  count={downgradeCount}
                  setCount={setDowngradeCount}
                  min={1}
                  max={selectedPlanToDowngrade?.quantity}
                  className="w-full"
                />
                {selectedPlanToDowngrade && (
                  <p className="text-xs text-muted-foreground mt-1">
                    You currently have {selectedPlanToDowngrade.quantity}{" "}
                    mailboxes. You cannot downgrade more than your current
                    quantity.
                  </p>
                )}
              </div>

              {/* Price and Feature Comparison */}
              {(downgradeOption === "plan" && targetPlanType) ||
              (downgradeOption === "cycle" &&
                interval !== selectedPlanToDowngrade?.interval &&
                getLostFeatures.length > 0) ? (
                <Card className="mt-4">
                  <CardHeader className="p-3 pb-0">
                    <CardTitle className="text-base text-destructive flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Features You'll Lose
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 p-3 pt-1 ">
                    {/* Features Lost - Only relevant for plan type downgrades */}
                    {downgradeOption === "plan" &&
                      getLostFeatures.length > 0 && (
                        <div className="space-y-2 mt-2">
                          <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                            <ul className="space-y-2">
                              {getLostFeatures.map((feature, idx) => (
                                <li
                                  key={idx}
                                  className="flex items-start gap-2 text-sm"
                                >
                                  <X className="h-4 w-4 mt-0.5 text-red-500 shrink-0" />
                                  <span className="text-red-700">
                                    {feature}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                  </CardContent>
                </Card>
              ) : null}
            </>
          )}
        </div>
      </ScrollArea>

      <DialogFooter className="px-6">
        <Button variant="outline" onClick={() => setShowDowngradeModal(false)}>
          Cancel
        </Button>

        <Button
          variant="default"
          onClick={handleDowngradePlan}
          disabled={isSubmitDisabled}
        >
          Continue to Downgrade
        </Button>
      </DialogFooter>
    </>
  );
};
